import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import * as Sentry from "@sentry/react";
import {
  MutateOptions,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import Cookie from "js-cookie";
import { noop } from "lodash/fp";

import apiClient from "@readai/common/api-client";
import Config from "@readai/common/config";
import useDetectedLogin from "@readai/common/hooks/useDetectedLogin";
import { toast } from "@readai/common/hooks/useToasts";
import { getLocaleOrFallback } from "@readai/common/i18n";
import ROUTES from "@readai/common/routes";
import { getLocalStorage } from "@readai/common/storage";
import { deleteAccount } from "@readai/common/tracking/events";
import Tracker from "@readai/common/tracking/Tracker";
import * as TrackingUtils from "@readai/common/tracking/utils";
import type {
  LocationWithState,
  ResetPasswordBody,
  SignInData,
  SignInUser,
  SignUpData,
  SignUpUser,
  User,
} from "@readai/common/types";
import {
  isResponseErrorWithStatus,
  populateUser,
  USER_REQUESTED_LOCALE,
  withQueryParamsUrl,
} from "@readai/common/utils";
import {
  getOauthUrl,
  getRedirectUrls,
  getSignupSourceFromLocation,
  setLoggedInCookie,
} from "@readai/common/utils/authUtils";
import { isLinkExternal } from "@readai/common/utils/urlUtils";

import useSyncBilling from "./billing/useSyncBilling";
import useLocalStorage from "./useLocalStorage";
import { setActiveUserId } from "../api/getApi";
import Link from "../components/Link";

type AuthErrors = { signUp?: { email: string } };

interface CommonUseAuth {
  fetchAndSetUser: () => Promise<User>;
  setUser: (user: User | null) => void;
  signIn: (
    data: SignInData,
    options: {
      onError?: MutateOptions<void, Error, SignInUser, unknown>["onError"];
    },
  ) => void;
  signUp: (
    data: SignUpData,
    options?: {
      onError?: MutateOptions<void, Error, SignUpUser, unknown>["onError"];
    },
  ) => void;

  signOut: ({
    redirect,
    revokeAll,
  }?: {
    redirect?: boolean;
    revokeAll?: boolean;
  }) => void;
  deleteAccount: UseMutationResult<
    void,
    Error,
    { comments: string; reason: string }
  >;
  clearUser: () => void;
  wipeUserState: (redirect?: boolean) => void;
  requestPasswordReset: UseMutationResult<
    void,
    Error,
    { email: string | null; recaptchaToken: string | null },
    unknown
  >;
  resetPassword: (data: ResetPasswordBody) => void;
  hasPreviousSignIn: boolean;
  impersonating: boolean;
  thirdPartyDataCollectionEnabled: boolean;
  isLoading: boolean;
  errors: AuthErrors;
  setErrors: Dispatch<SetStateAction<object>>;
  setImpersonating: (impersonating: boolean) => void;
}

interface UseAuth extends CommonUseAuth {
  user: User | null;
}

interface UseAuthenticated extends UseAuth {
  user: User;
}

const raiseProviderError = () => {
  throw Error("useAuth must be used within an AuthProvider");
};

const defaultContext: UseAuth = {
  user: null,
  hasPreviousSignIn: false,
  impersonating: false,
  thirdPartyDataCollectionEnabled: true,
  isLoading: false,
  errors: {},
  setErrors: noop,
  fetchAndSetUser: raiseProviderError,
  setUser: noop,
  clearUser: noop,
  wipeUserState: noop,
  signIn: raiseProviderError,
  signUp: raiseProviderError,
  signOut: raiseProviderError,
  deleteAccount: raiseProviderError as unknown as UseAuth["deleteAccount"],
  requestPasswordReset:
    noop as unknown as CommonUseAuth["requestPasswordReset"],
  resetPassword: noop,
  setImpersonating: noop,
};

const USER_STORAGE_KEY = "readUser";

// Used when we don't have access to local storage
let backupLocalUser: User | null = null;

/**
 * Location for redirect once an email/password login/signup is complete in order to close the web view within the mobile app.
 */
const completeMobileAppLogin = (returnUrl: string) =>
  (window.location.href = withQueryParamsUrl(
    `${Config.api.HOST}/users/me/token`,
    {
      return_url: returnUrl,
    },
  ));

const setLocalUser = (user: User | null) => {
  if (user) {
    try {
      getLocalStorage().setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } catch (error) {
      Sentry.captureException(error);
      backupLocalUser = user;
    }
  } else {
    try {
      getLocalStorage().removeItem(USER_STORAGE_KEY);
    } catch (error: unknown) {
      const errorMessage =
        "message" in (error as Error)
          ? (error as Error)?.message
          : // eslint-disable-next-line lingui/no-unlocalized-strings
            "unknown error";
      Sentry.addBreadcrumb({
        message: `Failed to set local user: ${errorMessage}`,
        level: "error",
      });
      Sentry.captureException(error);
      backupLocalUser = null;
    }
  }
};

const getLocalUser = () => {
  try {
    const jsonString = getLocalStorage().getItem(USER_STORAGE_KEY);
    if (jsonString) {
      // Ensures that we correctly upgrade any new fields on the user
      return populateUser<User>({ ...JSON.parse(jsonString), stale: true });
    }
    return null;
  } catch (error: unknown) {
    const errorMessage =
      "message" in (error as Error)
        ? (error as Error)?.message
        : // eslint-disable-next-line lingui/no-unlocalized-strings
          "unknown error";
    Sentry.addBreadcrumb({
      message: `Failed to get local user: ${errorMessage}`,
      level: "error",
    });
    Sentry.captureException(error);
    return backupLocalUser;
  }
};

const useProvideAuth = (): UseAuth => {
  const [user, setAuthUser] = useState<User | null>(getLocalUser());
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation() as LocationWithState;
  const queryClient = useQueryClient();
  const [queryParams] = useSearchParams();

  const [impersonating, setImpersonating] = useLocalStorage(
    "impersonating",
    false,
    { scope: "session", userDependent: false, user: null },
  );

  const detectedLogin = useDetectedLogin();

  const hasPersistedLogin = detectedLogin.persistedLogin != null;

  // TODO: should be able to deprecate this/eventually move to just using the
  //   persisted login. but we still need to maintain for backwards compatibility
  const [hasPreviousSignIn, setHasPreviousSignIn] = useLocalStorage(
    "hasPreviousSignIn",
    hasPersistedLogin,
    { userDependent: false, migrateKeys: true, user: null },
  );

  const userHasPreviousSignIn = hasPreviousSignIn || hasPersistedLogin;

  const toggleImpersonating = (impersonate: boolean) => {
    if (impersonate) {
      setImpersonating(true);
      Tracker.disable();
    } else {
      setImpersonating(false);
      Tracker.enable();
    }
  };

  const setUser = (updatedUser: User | null) => {
    TrackingUtils.configureThirdPartyDataCollection(updatedUser, user);

    setLocalUser(updatedUser);
    if (updatedUser) {
      setAuthUser({ ...updatedUser, stale: false });
    } else {
      setAuthUser(null);
    }
  };

  const queryParamObj = Object.fromEntries(queryParams.entries());

  const clearUser = useCallback(() => {
    const localUser = getLocalUser();
    if (localUser) {
      // this method gets called any time we detect that there is no user (i.e. any
      // time the user interacts with the application in an unauthenticated state),
      // however, we only want to reset the *tracker* user if we are specifically going
      // from an authenticated to an unauthenticated state (i.e. user logs out);
      // otherwise we want to persist the *same* anonymous user across page refreshes,
      // redirects, etc. until they have signed up/logged in so that mixpanel can
      // automatically merge *all* previous anonymous activity (and whatever properties came
      // with that activity, such as utm params) with the authenticated user
      Tracker.resetUser();
    }
    TrackingUtils.resetThirdPartyDataCollection();
    setLocalUser(null);
    setAuthUser(null);
    setActiveUserId();
    toggleImpersonating(false);
  }, []);

  const doSignIn = useMutation({
    mutationFn: apiClient.auth.signInUser,
    onSuccess: async (_response, data) => {
      const { returnUrl, hasMobileOverride } = getRedirectUrls(
        "signin",
        "email",
        queryParamObj,
        location,
        false,
      );

      setLoggedInCookie(true);

      if (hasMobileOverride) {
        return completeMobileAppLogin(returnUrl);
      }

      // save this as the user's preferred sign in method on successful sign in
      detectedLogin.setPersistedLogin({
        email: data.email,
        method: "password",
      });

      if (isLinkExternal(location?.state?.redirectTo)) {
        window.location.href = location?.state?.redirectTo?.pathname ?? "/";
      }

      await fetchAndSetUser();
      return navigate(returnUrl, { state: location.state });
    },
    onError: async (error) => {
      if (isResponseErrorWithStatus(error, 403)) {
        const json = await error.response.json();
        if (json?.error_reason === "saml_required" && json.redirect_url) {
          window.location.href = json.redirect_url;
          return;
        }
      }
      toast.error(
        msg`Something went wrong signing you in. Please ensure your email and password are correct and try again.`,
      );
    },
  });

  const signIn: CommonUseAuth["signIn"] = (data, options) => {
    if (data.action === "email") {
      doSignIn.mutate(data, options);
      return;
    }

    if (data.email) {
      // save this as the user's preferred sign-in provider
      detectedLogin.setPersistedLogin({
        email: data.email,
        method: data.action,
      });
    }

    if (data.action) {
      // if action is set we can update the window location
      window.location.href = getOauthUrl(
        data.action,
        "signin",
        {
          ...queryParamObj,
          ...(data.redirectTo ? { redirectTo: data.redirectTo } : {}),
        },
        location,
      );
    }
  };

  const doSignUp = useMutation({
    mutationFn: apiClient.auth.signUpUser,
    onSuccess: async (json) => {
      if (json.error) {
        setErrors({
          ...errors,
          // Email is the only error we handle right now
          signUp: { email: json.message },
        });
        return;
      }

      setErrors({ ...errors, signUp: {} });

      const { returnUrl, hasMobileOverride } = getRedirectUrls(
        "signup",
        "email",
        queryParamObj,
        location,
        false,
      );

      Sentry.addBreadcrumb({
        category: "auth",
        message: "Successful sign up",
        level: "info",
        data: {
          returnUrl,
          locationState: location.state,
        },
      });

      if (hasMobileOverride) {
        return completeMobileAppLogin(returnUrl);
      }

      navigate(returnUrl, { state: location.state });
    },
    onError: async (error) => {
      if (isResponseErrorWithStatus(error, 422)) {
        return navigate(ROUTES.SIGN_UP, { state: location.state });
      }

      if (isResponseErrorWithStatus(error, 403)) {
        const json = await error.response.json();
        if (json?.error_reason === "saml_required" && json.redirect_url) {
          toast.error({
            type: "system",
            title: msg`SAML Sign-Up Required`,
            message: msg`The workspace associated with your email domain requires signing up via SAML SSO.`,
            action: (
              <Link
                href={json.redirect_url}
                color="error.main"
                fontWeight={600}
                my={0.5}
              >
                <Trans>Sign Up with SAML</Trans>
              </Link>
            ),
            sticky: true,
            dismissible: true,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
          });

          return;
        }
      }

      toast.error(
        msg`Something went wrong signing you up. Please reach out to support@read.ai if the problem persists.`,
      );
    },
  });

  const signUp: CommonUseAuth["signUp"] = (data, options = {}) => {
    // if there was a report invite and the user was redirected to the sign in/up page,
    // the invite params will be in the location state
    const { source: signupSource, sourceId: signupSessionId } =
      getSignupSourceFromLocation(location);
    const preferredLanguage = getLocaleOrFallback(USER_REQUESTED_LOCALE);

    if (data.action === "email") {
      doSignUp.mutate(
        {
          ...data,
          signupSource,
          signupSessionId,
          preferredLanguage,
        },
        options,
      );
      return;
    } else if (data.action) {
      window.location.href = getOauthUrl(
        data.action,
        "signup",
        {
          ...queryParamObj,
          ...(data.redirectTo ? { redirectTo: data.redirectTo } : {}),
        },
        location,
        signupSource,
        signupSessionId,
        preferredLanguage,
        data.statsigStableId,
      );
    }
  };

  const wipeUserState = (redirect = true) => {
    try {
      setLoggedInCookie(false);
      queryClient.removeQueries();
      clearUser();
      Cookie.remove("user_cookie");
      if (redirect) {
        navigate(ROUTES.SIGN_IN);
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const signOut = ({
    redirect = true,
    revokeAll = false,
  }: { redirect?: boolean; revokeAll?: boolean } = {}) =>
    apiClient.auth.signOutUser(revokeAll).then(() => wipeUserState(redirect));

  // TODO: Use useQuery here instead to avoid duplicate calls to /user/me?
  const fetchAndSetUser = useCallback(async (): Promise<User> => {
    const user: User = populateUser(await apiClient.auth.getUser());
    if (user && !userHasPreviousSignIn) {
      setHasPreviousSignIn(true);
    }
    setUser(user);
    setActiveUserId(user?.id);
    return user;
  }, [impersonating, userHasPreviousSignIn]);

  const requestPasswordReset: CommonUseAuth["requestPasswordReset"] =
    useMutation({
      mutationFn: async ({ email, recaptchaToken }) => {
        return apiClient.auth.postRequestPasswordReset(email, recaptchaToken);
      },
    });

  // TODO: useMutation
  const resetPassword = async (data: ResetPasswordBody) => {
    if (!data) return;
    try {
      await apiClient.auth.postResetPassword(data);
      toast.success(
        msg`Password successfully reset. You may now sign in using your new password.`,
      );
      navigate(ROUTES.SIGN_IN, { replace: true });
    } catch {
      toast.error(
        msg`The token you provided has either expired or is invalid. Please request a new password reset link.`,
      );
    }
  };

  const deleteAccountMutation = useMutation({
    mutationFn: async ({
      reason,
      comments,
    }: {
      reason: string;
      comments: string;
    }) => {
      deleteAccount({ userId: user?.id ?? "no_user", reason, comments });
      await apiClient.auth.deleteAccount();
    },
    retry: 3, // delete is idempotent so ok to retry
    onSuccess: () => {
      // remove any knowledge of a previous sign in
      setHasPreviousSignIn(false);
      detectedLogin.setPersistedLogin(null);
      wipeUserState(false);
      // forces page refresh which will ensure 1) a clean location state and 2) that they land on
      // the sign up page; using a `navigate` redirect gets caught by the RequireUser path which will
      // redirect to the sign in page instead
      window.location.href = ROUTES.SIGN_UP;
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  useEffect(() => {
    // Skip all /go/ links and the secure sign in link
    // TODO: need a better way to go about this if we add more routes
    if (
      window.location.pathname.startsWith("/go") ||
      window.location.pathname.includes("secure_signin")
    ) {
      return;
    }

    // check if user session is still valid by making a
    // request to /user/me (this will send the httponly cookie)
    // if the session is not valid, set the user to null
    // we don't want to redirect here because some routes may be valid without
    // a valid user
    fetchAndSetUser().catch((error) => {
      if (isResponseErrorWithStatus(error, 401)) {
        clearUser();
      }
    });
  }, [fetchAndSetUser, clearUser]);

  const isLoading = doSignUp.isPending || doSignIn.isPending;

  return {
    user,
    hasPreviousSignIn: hasPreviousSignIn || hasPersistedLogin,
    impersonating,
    thirdPartyDataCollectionEnabled:
      TrackingUtils.isThirdPartyDataCollectionEnabled(user),
    isLoading,
    errors,
    setUser,
    setErrors,
    fetchAndSetUser,
    clearUser,
    signIn,
    signUp,
    signOut,
    deleteAccount: deleteAccountMutation,
    requestPasswordReset,
    resetPassword,
    setImpersonating: toggleImpersonating,
    wipeUserState,
  };
};

const AuthContext = createContext(defaultContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const auth = useProvideAuth();
  const syncBilling = useSyncBilling();
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!auth.user || auth.user.stale || hasSynced.current) return;

    // when we first detect a logged-in user, post to the billing sync endpoint to
    // ensure the user's billing account is in a valid state
    hasSynced.current = true;
    syncBilling.mutate();
  }, [Boolean(auth.user?.stale)]);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

// Heavily inspired by https://usehooks.com/useAuth/
const useAuth = () => useContext(AuthContext);

export const useAuthenticated = () => useAuth() as UseAuthenticated;
export default useAuth;
